import { useState } from 'react';
import { ArrowRight, CheckCircle, X, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from './moana-data';

export const MoanaSubscriptionModal = ({ packName, onClose }) => {
  const [modalForm, setModalForm] = useState({ name: '', company: '', phone: '', email: '', effectif: '', engagement: '6-mois', message: '' });
  const [modalSuccess, setModalSuccess] = useState(false);
  const [modalSubmitting, setModalSubmitting] = useState(false);

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    if (!modalForm.name || !modalForm.email || !modalForm.phone) {
      toast.error('Veuillez remplir les champs obligatoires'); return;
    }
    setModalSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: modalForm.name, email: modalForm.email, phone: modalForm.phone,
        company: modalForm.company, subject: `Moana Paye - Souscription ${packName}`,
        message: `Pack: ${packName}\nEffectif: ${modalForm.effectif}\nEngagement: ${modalForm.engagement}\n\n${modalForm.message}`
      });
      setModalSuccess(true);
      toast.success('Pré-inscription envoyée !');
    } catch { toast.error("Erreur lors de l'envoi."); }
    finally { setModalSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/65 backdrop-blur-sm flex items-center justify-center p-5" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="modal-overlay">
      <div className="bg-white rounded-xl max-w-[520px] w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-200" data-testid="modal-content">
        <div className="bg-[#0F766E] p-7 relative sticky top-0 z-10 rounded-t-xl">
          <button onClick={onClose} className="absolute top-5 right-5 w-8 h-8 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors" data-testid="modal-close">
            <X className="w-4 h-4" />
          </button>
          <h3 className="text-xl font-bold text-white">Souscrire au {packName}</h3>
          <p className="text-xs text-white/50 mt-1">Pre-inscription — nous vous recontactons sous 24 h</p>
        </div>
        <div className="p-7">
          {!modalSuccess ? (
            <>
              <div className="bg-[#F8FAFC] rounded-lg p-4 text-sm text-gray-500 leading-relaxed mb-6">
                <strong className="text-[#1F2937] block mb-1">Comment ca marche :</strong>
                remplissez ce formulaire → nous validons votre dossier → vous recevez un lien de paiement sécurisé par email. Votre abonnement démarre à réception du premier paiement.
              </div>
              <form onSubmit={handleModalSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-gray-400 text-[10px] uppercase tracking-wider">Prenom & Nom *</Label>
                    <Input value={modalForm.name} onChange={(e) => setModalForm({ ...modalForm, name: e.target.value })} placeholder="Votre nom" className="mt-1" required data-testid="modal-name" />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-[10px] uppercase tracking-wider">Entreprise</Label>
                    <Input value={modalForm.company} onChange={(e) => setModalForm({ ...modalForm, company: e.target.value })} placeholder="Votre structure" className="mt-1" data-testid="modal-company" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-gray-400 text-[10px] uppercase tracking-wider">Telephone *</Label>
                    <Input type="tel" value={modalForm.phone} onChange={(e) => setModalForm({ ...modalForm, phone: e.target.value })} placeholder="87 XX XX XX" className="mt-1" required data-testid="modal-phone" />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-[10px] uppercase tracking-wider">Email *</Label>
                    <Input type="email" value={modalForm.email} onChange={(e) => setModalForm({ ...modalForm, email: e.target.value })} placeholder="votre@email.com" className="mt-1" required data-testid="modal-email" />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-400 text-[10px] uppercase tracking-wider">Effectif salarié *</Label>
                  <Select value={modalForm.effectif} onValueChange={(v) => setModalForm({ ...modalForm, effectif: v })}>
                    <SelectTrigger className="mt-1" data-testid="modal-effectif">
                      <SelectValue placeholder="- Selectionner -" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-5">1 – 5 salariés</SelectItem>
                      <SelectItem value="6-14">6 – 14 salariés</SelectItem>
                      <SelectItem value="15-25">15 – 25 salariés</SelectItem>
                      <SelectItem value="26+">26 salariés et +</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-400 text-[10px] uppercase tracking-wider">Engagement souhaite</Label>
                  <Select value={modalForm.engagement} onValueChange={(v) => setModalForm({ ...modalForm, engagement: v })}>
                    <SelectTrigger className="mt-1" data-testid="modal-engagement">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6-mois">6 mois (préavis 2 mois)</SelectItem>
                      <SelectItem value="12-mois">12 mois (préavis 3 mois)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-400 text-[10px] uppercase tracking-wider">Situation actuelle (optionnel)</Label>
                  <textarea value={modalForm.message} onChange={(e) => setModalForm({ ...modalForm, message: e.target.value })} rows={3} placeholder="Décrivez votre contexte..."
                    className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F59E0B] resize-y" data-testid="modal-message" />
                </div>
                <Button type="submit" disabled={modalSubmitting} className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-white font-bold py-5" data-testid="modal-submit">
                  {modalSubmitting ? 'Envoi en cours...' : 'Envoyer ma pre-inscription'} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <p className="text-[11px] text-gray-400 text-center flex items-center justify-center gap-1.5">
                  <Lock className="w-3 h-3" /> Aucun prélèvement immédiat. Vous recevrez un lien de paiement sécurisé après validation de votre dossier.
                </p>
              </form>
            </>
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-[#1F2937] mb-2">Pre-inscription envoyee !</h3>
              <p className="text-sm text-gray-500 leading-relaxed">
                Nous avons bien reçu votre demande.<br />
                Vous recevrez sous <strong>24 h ouvrées</strong> un email de confirmation avec le lien de paiement sécurisé.
              </p>
              <Button onClick={onClose} className="mt-6 bg-[#0D9488] hover:bg-[#0F766E] text-white px-8">Fermer</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
